/*
 * Copyright © 2018-2026, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { ReactElement, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { petSpecies, strings } from "../../../common/Strings/Strings";
import Button from "../../../components/Button";
import PetFormOptionalTab from "./PetFormOptionalTab";
import AlertBox from "../../../components/AlertBox";
import { PetFormProps } from "./PetForm";
import PetFormMandatoryTab from "./PetFormMandatoryTab";
import { PersonDetailsResponse } from "../../../models/contact/PersonDetailsResponse";
import LoaderInline from "../../../components/LoaderInline";
import UserProfilePicture from "../../../components/Pictures/User/UserProfilePicture";
import { PetVetApiResponse } from "../../../models/pet/PetVetDataResponse";
import ChipSearch from "../../PetOwner/ClinicPetOwner/CreatePetOwner/ChipSearch";
import { useClinic } from "../../../contexts/ClinicContext";
import { transformStringToPetSpecies } from "../../../models/species/PetVetDataSpeciesCategory";
import moment from "moment";
import { convertFromPetVetDataGender } from "../../../models/pet/PetGender";
import { StringMap } from "../../../models/calendar/AppointmentFormData";

interface Props {
  error: string | null;
  onSubmit(pet: PetFormProps): void;
  ownerPictureUserId?: string;
  petOwner?: PersonDetailsResponse;
  isLoading: boolean;
  isPetOwnerLoading: boolean;
}

enum Tabs {
  Mandatory,
  Optional,
}

const tabsInOrder = [Tabs.Mandatory, Tabs.Optional];

const TabbedPetForm = ({
  error,
  onSubmit,
  ownerPictureUserId,
  petOwner,
  isLoading,
  isPetOwnerLoading,
}: Props): ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const petvetdata = location.state?.petvetdata;
  const useFormValues = useForm<PetFormProps>({
    mode: "onSubmit",
    defaultValues: {
      breed: [],
      color: undefined,
      dateOfBirth: undefined,
      dateOfBirthType: "unknown",
      estimatedAge: {
        months: 0,
        years: 0,
      },
      gender: undefined,
      name: "",
      species: [],
      chipNumber: undefined,
      chronicDiseases: undefined,
      comment: undefined,
      deceased: false,
      insuranceCompany: undefined,
      insuranceNumber: undefined,
      passportNumber: undefined,
      tattooNumber: undefined,
    },
  });

  const {
    handleSubmit,
    trigger,
    formState: { isSubmitting },
    setValue,
    reset,
  } = useFormValues;

  const [activeTab, setActiveTab] = useState<Tabs>(tabsInOrder[0]);
  const [tabIndex, setTabIndex] = useState<number>(0);
  const { clinic } = useClinic();

  const fillForm = (input?: StringMap | null) => {
    if (!input) {
      reset();
    } else {
      setValue("name", input?.allat_nev);
      setValue("species", [
        {
          value: transformStringToPetSpecies(input?.allat_faj),
          name: petSpecies[transformStringToPetSpecies(input?.allat_faj)],
        },
      ]);
      if (input?.allat_fajta) {
        setValue("breed", [{ name: input?.allat_fajta }]);
      }
      setValue("gender", convertFromPetVetDataGender(input?.allat_ivar, input?.allat_ivart === "igen"));
      const dateOfBirth = input?.allat_szul_dat;
      if (dateOfBirth) {
        setValue("dateOfBirth", moment(dateOfBirth).format());
        setValue("dateOfBirthType", input?.allat_szul_becs === "igen" ? "estimated" : "known");
      }
      setValue("color", input?.allat_szin);
      setValue("chipNumber", input?.allat_chip_szam);
      setValue("passportNumber", input?.utlevel_szam || undefined);
      setValue("tattooNumber", input?.allat_tet_szam || undefined);
      setValue("comment", input?.allat_megj || undefined);
      setValue("deceased", input?.allat_elh === "igen");
    }
  };

  useEffect(() => {
    if (petvetdata) {
      fillForm(petvetdata?.data);
    }
  }, [petvetdata]);

  const goToPreviousTab = () => {
    if (tabIndex <= 0) return;
    setActiveTab(tabsInOrder[tabIndex - 1]);
    setTabIndex(tabIndex - 1);
  };

  const goToNextTab = () => {
    if (tabIndex >= tabsInOrder.length - 1) return;
    setActiveTab(tabsInOrder[tabIndex + 1]);
    setTabIndex(tabIndex + 1);
  };

  return (
    <form className="lg:tw-card w-full space-y-6 lg:w-auto lg:p-8" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex justify-between space-x-4 items-end">
        <h1 className="text-xl font-semibold leading-tight text-zinc-800 dark:text-white lg:text-2xl">
          {strings.addNewPet}
        </h1>
        <span className="text-gray-500 dark:text-gray-400">
          {tabIndex + 1}/{tabsInOrder.length}
        </span>
      </div>
      {petOwner || isPetOwnerLoading ? (
        <>
          {isPetOwnerLoading ? (
            <div className="flex justify-center">
              <LoaderInline />
            </div>
          ) : (
            <>
              <div className="flex items-center">
                <div style={{ width: "75px", height: "75px" }}>
                  <UserProfilePicture border userId={ownerPictureUserId} />
                </div>
                <div className="flex flex-col ml-3">
                  <Link to={`/pet-owner-profile/${petOwner?.id}`}>{petOwner?.fullName}</Link>
                  {petOwner?.phones[0]?.value}
                </div>
              </div>
              <hr className="h-px bg-gray-200 border-0 dark:bg-gray-700" />
            </>
          )}
        </>
      ) : (
        <></>
      )}
      <AlertBox message={error} />
      {clinic?.countryCode === "HU" && (
        <ChipSearch onSelectChipData={(data) => fillForm(data?.data)} previouslySelectedResult={petvetdata} />
      )}
      <FormProvider {...useFormValues}>
        {(() => {
          switch (activeTab) {
            case Tabs.Mandatory:
              return (
                <>
                  <PetFormMandatoryTab />
                  <Button
                    key="saveAndContinue"
                    onClick={() => {
                      const response = trigger([
                        "name",
                        "species",
                        "breed",
                        "gender",
                        "color",
                        "dateOfBirthType",
                        "dateOfBirth",
                      ]);

                      response.then((isValid: boolean) => {
                        if (isValid) {
                          goToNextTab();
                        }
                      });
                    }}
                  >
                    {strings.saveAndContinue}
                  </Button>
                  <Button
                    className="text-left text-sm"
                    onClick={() => {
                      navigate(location.state?.from ?? "/");
                    }}
                    variant="link"
                  >
                    {strings.cancel}
                  </Button>
                </>
              );
            case Tabs.Optional:
              return (
                <>
                  <PetFormOptionalTab />
                  <Button disabled={isSubmitting} key="saveAndCreate" loading={isLoading} type="submit">
                    {strings.saveAndCreate}
                  </Button>
                  <Button className="text-left text-sm" onClick={goToPreviousTab} variant="link">
                    {strings.back}
                  </Button>
                </>
              );
            default:
              return <></>;
          }
        })()}
      </FormProvider>
    </form>
  );
};

export default TabbedPetForm;
